import { Expression } from '../../builder/expression';

export const GROUPING = ['(', ')', '[', ']', '{', '}', ',', '|'] as const;

export type Grouping = (typeof GROUPING)[number];

export const isGrouping = (x: string): x is Grouping =>
  GROUPING.includes(x as any);

export const OPERATORS = [
  '|>',
  '??',
  '||',
  '&&',
  '==',
  '!=',
  '>=',
  '<=',
  '>',
  '<',
  '+',
  '*',
  '-',
  '/',
  '^',
  '=',
  ':',
  '!',
  '?',
  ';',
  '...',
] as const;

export type Operator = (typeof OPERATORS)[number];
export type Keyword = (typeof KEYWORDS)[number];

export const isKw = (x: string): x is Keyword => KEYWORDS.includes(x as any);

export const isOperator = (x: string): x is Operator =>
  OPERATORS.includes(x as any);

export const KEYWORDS = ['let', 'ref'] as const;

export type KwToken = {
  readonly t: 'keyword';
  readonly kw: (typeof KEYWORDS)[number];
};

export type TagToken = {
  readonly t: 'tag';
  readonly tag: string;
};

export type CommentToken = {
  readonly t: 'comment';
  readonly direction: 'leading' | 'trailing';
  readonly contents: string;
};

export type GroupingToken = {
  readonly t: 'grouping';
  readonly op: Grouping;
};

/**
 * e.g.
 * ```
 * foo
 * ```
 */
export type SymbolToken = {
  readonly t: 'symbol';
  readonly sym: string;
};

export type OperatorToken = {
  readonly t: 'operator';
  readonly op: Operator;
};

/**
 * e.g.
 * ```
 * "attr"  // rel"attr"
 * ```
 */
export type IdentToken = {
  readonly t: 'ident';
  readonly name: string;
};

/**
 * e.g.
 * ```
 * hello
 * 'hello'
 * true
 * 5
 * ```
 *
 * Todo: more specs on date literals
 */
export type LiteralToken = {
  readonly t: 'literal';
  readonly val: string | boolean | number | Date;
};

export type RawExpressionToken = {
  readonly t: 'raw-expression';
  readonly val: Expression;
};

export type TemplateTokenKind = 'start' | 'continue' | 'end' | 'complete';

export type TemplateToken = {
  readonly t: 'template';
  readonly contents: string;
  readonly kind: TemplateTokenKind;
};

export type WhiteSpaceToken = {
  readonly t: 'white-space';
  readonly space: string;
};

export type UnknownToken = {
  readonly t: 'unknown';
  readonly found: string;
};

export type NongroupingToken =
  | SymbolToken
  | OperatorToken
  | IdentToken
  | LiteralToken
  | RawExpressionToken
  | WhiteSpaceToken
  | KwToken
  | TemplateToken
  | TagToken
  | CommentToken
  | UnknownToken;

export type Token = GroupingToken | NongroupingToken;

export const kw = (kw: string): KwToken => {
  if (isKw(kw)) {
    return { t: 'keyword', kw };
  } else {
    const error = new Error(`"${kw}" is not a valid keyword`);

    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(error, op);
    }

    throw error;
  }
};

export const op = (opToken: string): OperatorToken => {
  if (isOperator(opToken)) {
    return { t: 'operator', op: opToken };
  } else {
    const error = new Error(`"${opToken}" is not a valid operator`);
    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(error, op);
    }
    throw error;
  }
};

export const group = (opToken: string): GroupingToken => {
  if (isGrouping(opToken)) {
    return { t: 'grouping', op: opToken };
  } else {
    const error = new Error(`"${opToken}" is not a valid grouping`);
    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(error, op);
    }
    throw error;
  }
};

export function tag(tag: string): TagToken {
  return { t: 'tag', tag };
}

export function unknown(found: string): UnknownToken {
  return { t: 'unknown', found };
}
export function lit(val: string | number | boolean | Date): LiteralToken {
  return { t: 'literal', val };
}
export function ident(name: string): IdentToken {
  return { t: 'ident', name };
}
export function ws(space: string): WhiteSpaceToken {
  return { t: 'white-space', space };
}
export function rawExpr(val: Expression): RawExpressionToken {
  return { t: 'raw-expression', val };
}
export function sym(sym: string): SymbolToken {
  return { t: 'symbol', sym };
}

export function comment(
  contents: string,
  direction: 'leading' | 'trailing' // TODO: can add a "program-level" kind, which is a leading comment but does not render a preceeding newline in the formatter.
): CommentToken {
  return { t: 'comment', direction, contents };
}

export function template(
  contents: string,
  kind: TemplateTokenKind
): TemplateToken {
  return { t: 'template', contents, kind };
}

export function tStart(contents: string): TemplateToken {
  return { t: 'template', contents, kind: 'start' };
}

export function tEnd(contents: string): TemplateToken {
  return { t: 'template', contents, kind: 'end' };
}

export function tContinue(contents: string): TemplateToken {
  return { t: 'template', contents, kind: 'continue' };
}

export function tComplete(contents: string): TemplateToken {
  return { t: 'template', contents, kind: 'complete' };
}
